/**
 * TriggerZoneManager: box-shaped trigger regions.
 *
 * Each frame tests whether the player position is inside each box zone and
 * emits ZONE_ENTER / ZONE_EXIT via the EventBus.
 */

import { Vec3 } from 'playcanvas';
import { eventBus } from '../events/EventBus';
import type { TriggerZoneConfig } from '../config/manifest';

export class TriggerZoneManager {
    private _zones: TriggerZoneConfig[];
    private _inside = new Set<string>();

    constructor(zones: TriggerZoneConfig[]) {
        this._zones = zones;
    }

    update(playerPosition: Vec3) {
        for (const z of this._zones) {
            const inside = this._isInsideBox(playerPosition, z.center, z.size);
            const wasInside = this._inside.has(z.id);
            if (inside && !wasInside) {
                this._inside.add(z.id);
                eventBus.emit({ type: 'ZONE_ENTER', id: z.id, name: z.name });
            } else if (!inside && wasInside) {
                this._inside.delete(z.id);
                eventBus.emit({ type: 'ZONE_EXIT', id: z.id, name: z.name });
            }
        }
    }

    private _isInsideBox(p: Vec3, center: number[], size: number[]): boolean {
        return (
            Math.abs(p.x - center[0]) <= size[0] / 2 &&
            Math.abs(p.y - center[1]) <= size[1] / 2 &&
            Math.abs(p.z - center[2]) <= size[2] / 2
        );
    }

    getZone(id: string): TriggerZoneConfig | undefined {
        return this._zones.find((z) => z.id === id);
    }

    /** Clear trigger state (on scene unload). */
    destroy() {
        this._inside.clear();
    }
}
